import React, { useState, useEffect } from 'react';
import { Grid, GridItem, Box, useBreakpointValue, Spinner, Alert, AlertIcon, Center } from '@chakra-ui/react';
import JobOpeningFilter from './JobOpeningFilter';
import JobOpeningSortBar from './JobOpeningSortBar';
import JobOpeningList from './JobOpeningList';
import { getJobCirculars } from '../../../services/jobCircularsService';
import { getLocations, getLocationNames } from '../../../services/locationsService';

const defaultLocations = [
  'Development Center, Rajshahi',
  'Mirpur DOHS, Dhaka',
  'Kawranbazar, Dhaka',
  'Banani, Dhaka',
  'Chittagong Office',
  'Sylhet Branch',
];

const JobOpeningLayout = ({ defaultTypes = [], fromInternship }) => {
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allLocationsFromAPI, setAllLocationsFromAPI] = useState(defaultLocations);
  const [allTypesFromAPI] = useState(['Fulltime', 'Part-time', 'Internship', 'Remote', 'Contract', 'Temporary']);
  const [allExperiencesFromAPI] = useState(['Fresher', 'Intermediate', 'No-Experience', 'Internship', 'Expert', 'Senior', 'Junior']);
  const [allPeriodsFromAPI] = useState(['Weekly', 'Monthly', 'Hourly']);

  const [location, setLocation] = useState('');
  const [types, setTypes] = useState(defaultTypes);
  const [experiences, setExperiences] = useState([]);
  const [salaryRange, setSalaryRange] = useState([0, 150000]);
  const [salaryPeriod, setSalaryPeriod] = useState(''); // Changed from 'Monthly' to empty string

  // Fetch locations and jobs data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First fetch locations
        const locationsResult = await getLocations();
        let fetchedLocations = [];
        let availableLocationNames = defaultLocations;

        if (locationsResult.success && locationsResult.locations.length > 0) {
          fetchedLocations = locationsResult.locations;
          availableLocationNames = getLocationNames(fetchedLocations);
          setLocations(fetchedLocations);
          setAllLocationsFromAPI(availableLocationNames);
        }

        // Then fetch jobs with location data
        const jobsResult = await getJobCirculars(fetchedLocations);
        if (jobsResult.success) {
          setJobs(jobsResult.jobs);
          
          // Extract unique locations from job data as fallback
          const uniqueJobLocations = [...new Set(jobsResult.jobs.map(job => job.location))].filter(Boolean);
          
          // Use API locations if available, otherwise fall back to job locations
          if (availableLocationNames.length > 0) {
            setAllLocationsFromAPI(availableLocationNames);
          } else {
            setAllLocationsFromAPI(uniqueJobLocations.length > 0 ? uniqueJobLocations : defaultLocations);
          }
          
          // Don't set default location - let all jobs display initially
          // Users can manually select a location to filter if needed
          
          setError(null);
        } else {
          setError(jobsResult.error || 'Failed to fetch jobs');
        }
      } catch (err) {
        setError('Failed to fetch data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [minSalary, maxSalary] = salaryRange;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 20;

  // Filtering logic - Hide expired jobs
  const filteredJobs = jobs.filter(job => {
    const currentDate = new Date();
    const jobDeadline = job.deadline ? new Date(job.deadline + 'T23:59:59') : null; // Add end of day time
    
    // Hide jobs that have expired (deadline has passed)
    const isNotExpired = !jobDeadline || jobDeadline >= currentDate;
    
    return (
      isNotExpired && // Only show non-expired jobs
      (location ? job.location === location : true) &&
      (types.length ? types.includes(job.type) : true) &&
      (experiences.length ? experiences.includes(job.experience) : true) &&
      (salaryPeriod ? job.period === salaryPeriod : true)
    );
  });

  const totalJobs = filteredJobs.length;
  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [location, types, experiences, minSalary, maxSalary, salaryPeriod]);

  // Responsive layout configuration
  const containerPadding = useBreakpointValue({ 
    base: 2,  // Mobile
    sm: 4,    // Small tablets
    md: 6,    // Tablets
    lg: 8     // Desktop
  });

  return (
    <Box 
      maxW={{ base: "100%", md: "1400px" }} 
      mx="auto" 
      px={containerPadding}
      py={{ base: 4, md: 6 }}
      width="100%" 
      overflowX="auto" 
    >
      <Box 
        bg="white" 
        borderRadius={{ base: "md", md: "lg" }} 
        boxShadow="md" 
        p={{ base: 2, sm: 4, md: 6 }} 
        overflow="hidden"
      >
        {loading ? (
          <Center py={10}>
            <Spinner size="xl" color="#000B8C" />
          </Center>
        ) : error ? (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        ) : (
          <Grid 
            templateColumns={{ 
              base: "1fr",           // Mobile: Stack vertically
              md: "280px 1fr",      // Tablet: Side-by-side with narrow filter
              lg: "320px 1fr"       // Desktop: Side-by-side with full filter
            }}
            gap={{ 
              base: 4,              // Mobile: Smaller gap
              md: 6,               // Tablet/Desktop: Larger gap
            }}
            w="100%"
            width="100%" 
            overflowX="auto" 
          >
            <GridItem>
              <Box 
                position={{ base: "static", md: "sticky" }}
                top="20px"
                overflowY={{ base: "visible", md: "visible" }} 
                pr={{ base: 0, md: 2 }}
              >
                <JobOpeningFilter
                  location={location}
                  setLocation={setLocation}
                  allLocations={allLocationsFromAPI}
                  types={types}
                  setTypes={setTypes}
                  allTypes={allTypesFromAPI}
                  experiences={experiences}
                  setExperiences={setExperiences}
                  allExperiences={allExperiencesFromAPI}
                  minSalary={minSalary}
                  maxSalary={maxSalary}
                  setSalaryRange={setSalaryRange}
                  salaryPeriod={salaryPeriod}
                  setSalaryPeriod={setSalaryPeriod}
                  allPeriods={allPeriodsFromAPI}
                />
              </Box>
            </GridItem>
            <GridItem>
              <Box>
                <JobOpeningSortBar totalJobs={totalJobs} />
                <JobOpeningList
                  jobs={filteredJobs}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  jobsPerPage={jobsPerPage}
                  onPageChange={setCurrentPage}
                  totalJobs={totalJobs}
                  fromInternship={fromInternship}
                />
              </Box>
            </GridItem>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default JobOpeningLayout;
